import { useDataStore, useFilterStore } from "@/stores";
import { useEditor, useNode } from "@craftjs/core";
import { ReactNode } from "react";
import Settings from "./Settings";

interface FilterContainerProps {
    filterField: string | null;
    filterValue: string | null;
    children: ReactNode;
}

const FilterContainer = ({ filterField = null, filterValue = null, children }: Partial<FilterContainerProps>) => {
    const {
        connectors: { connect },
    } = useNode();

    const { enabled } = useEditor((state) => ({
        enabled: state.options.enabled,
    }));

    const software = useDataStore((x) => x.software);
    const activeVersion = useFilterStore((state) => state.activeVersion);
    const activeSoftware = useFilterStore((state) => state.activeSoftware);
    const activeIfcVersion = useFilterStore((state) => state.activeIfcVersion);

    const shouldRender = () => {
        if (enabled) return true;
        if (!filterField || !filterValue) return true;

        // TODO: Future, implement filtering of other filter options?
        const softwareList = software[activeVersion!];
        if (!softwareList || softwareList.length === 0) return false;

        const filteredSoftware = software[activeVersion!].filter((x) => x.active);
        if (!filteredSoftware.length) return false;

        const activeItems = filteredSoftware.filter((x) => x.id === activeSoftware || x.id === activeIfcVersion);
        switch (filterValue) {
            case "no-software":
                return !activeItems.some((x) => !x.isIfc);

            case "no-ifc":
                return !activeItems.some((x) => x.isIfc);

            case "no-selection":
                return activeItems.length === 0;

            default:
                return activeItems.some((x) => x.name === filterValue);
        }
    };

    if (enabled && !children)
        return (
            <div ref={(ref) => connect(ref!)} className="w-full p-3 bg-gray-200 rounded animate-pulse">
                This is an empty filter container, add a 'button container' or 'text container' component here.
            </div>
        );

    if (shouldRender())
        return (
            <div ref={(ref) => connect(ref!)} className="w-full grid gap-6">
                {children}
            </div>
        );

    return null;
};

export default FilterContainer;

FilterContainer.craft = {
    displayName: "Filter Container",
    props: {
        filterField: null,
        filterValue: null,
    },
    related: {
        settings: Settings,
    },
    rules: {
        canDrag: () => true,
        canMoveOut: () => true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        canDrop: (targetNode: any) => targetNode.data.name === "ContentContainer",
    },
};
